/**
 * Module singleton for APT overlay lanes (consent / popups).
 * Flag-gated: when coordinator disabled, acquire always succeeds.
 */

import {
  canAcquireOverlay,
  shouldPreemptOverlay,
  type OverlayClaim,
  type OverlayLane,
} from '../../shared/adsense/overlayPriority';
import { isViteAptOverlayCoordinatorEnabled } from '../../shared/adsense/adSurfacePolicy';

type Listener = (active: OverlayClaim | null) => void;

let activeClaim: OverlayClaim | null = null;
const listeners = new Set<Listener>();

function notify(): void {
  listeners.forEach((l) => {
    try {
      l(activeClaim);
    } catch {
      // ignore listener errors
    }
  });
}

function readViteOverlayFlag(): string | boolean | undefined {
  // Avoid import.meta in this module so Jest (CJS) can load it.
  if (typeof process !== 'undefined' && process.env?.VITE_APT_OVERLAY_COORDINATOR_ENABLED != null) {
    return process.env.VITE_APT_OVERLAY_COORDINATOR_ENABLED;
  }
  return undefined;
}

function coordinatorEnabled(): boolean {
  if (isViteAptOverlayCoordinatorEnabled(readViteOverlayFlag())) {
    return true;
  }
  if (typeof process !== 'undefined' && process.env?.APT_OVERLAY_COORDINATOR_ENABLED === 'true') {
    return true;
  }
  return false;
}

/** Call from Vite/React entry to seed process.env for coordinator flag (optional). */
export function configureOverlayCoordinatorFromViteEnv(
  viteValue: string | boolean | undefined,
): void {
  if (typeof process !== 'undefined' && process.env && viteValue != null) {
    process.env.VITE_APT_OVERLAY_COORDINATOR_ENABLED = String(viteValue);
  }
}

export function getActiveOverlayClaim(): OverlayClaim | null {
  return activeClaim;
}

export function subscribeOverlayClaims(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Try to acquire an overlay lane. Returns true if caller may show UI.
 * When coordinator flag is off, always returns true (legacy parallel popups).
 */
export function acquireOverlay(lane: OverlayLane, id: string): boolean {
  const requested: OverlayClaim = { lane, id };
  if (!coordinatorEnabled()) {
    activeClaim = requested;
    notify();
    return true;
  }
  if (!canAcquireOverlay(activeClaim, requested)) {
    return false;
  }
  if (shouldPreemptOverlay(activeClaim, requested) || !activeClaim) {
    activeClaim = requested;
    notify();
    return true;
  }
  // Same lane same id
  if (activeClaim.lane === lane && activeClaim.id === id) {
    return true;
  }
  return false;
}

/** Release only if the caller still owns the claim. */
export function releaseOverlay(lane: OverlayLane, id: string): void {
  if (!activeClaim) {
    return;
  }
  if (activeClaim.lane === lane && activeClaim.id === id) {
    activeClaim = null;
    notify();
  }
  if (!coordinatorEnabled()) {
    // Keep notify for subscribers even in legacy mode when releasing
  }
}

/** Test helper — reset store between tests. */
export function __resetOverlayAdCoordinatorForTests(): void {
  activeClaim = null;
  listeners.clear();
}
